import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import InfoIcon from "@material-ui/icons/Info";
import A_SimpleDialog from "../../../Atoms/Feedback/Dialogs/A_SimpleDialog.jsx";
import A_Button from "../../../Atoms/Inputs/Buttons/A_Button";
import {
  defaultContactId,
  defaultAddressId,
  validateLoanDetailsDraft,
} from "../../../../../domain/professionalContacts/professionalContacts.rules";
import {
  LOAN_DETAIL_ROLE_KEYS,
  LOAN_DETAIL_ROLE_LABELS,
  LOAN_DETAIL_REQUIRED_ROLES,
} from "../../../../../domain/professionalContacts/professionalContacts.types";
import { api, normaliseDetails } from "./ProfessionalDetails.helper";
import RoleEditFields from "./RoleEditFields";
import QuotesEditor from "./QuotesEditor";
import useStyles from "./ProfessionalDetails.styles";
import A_SecuredAction from "../../../Atoms/Security/A_SecuredAction";
import { pageSecurityActions } from "../../../../../Utils/Helpers/pageSecurity";

const BANK_ROLE_KEYS = ["bankSolicitor", "bankValuer", "monitoringSurveyor"];
// Receiver (v1.4.0) is wired up exactly like Administrator — same firm/individual/address
// picker, no lifecycle dates — per the "Receiver linked with Professional Details" request.
const REPOSITORY_ROLE_KEYS = [
  "broker",
  "borrowerSolicitor",
  "administrator",
  "receiver",
];

/**
 * Standalone "Edit Professional Details" dialog (§4b) — ONLY the professional-details form,
 * not the merged "Edit Loan" accordion (that stays on the Loan Overview screen's own modal).
 */
const EditProfessionalDetailsDialog = ({
  loanId,
  initialDetails,
  onClose,
  onSaved,
}) => {
  const classes = useStyles();
  const [draft, setDraft] = useState(() =>
    JSON.parse(JSON.stringify(initialDetails))
  );
  const [firmDetailsById, setFirmDetailsById] = useState({});
  const [saving, setSaving] = useState(false);
  const [missing, setMissing] = useState([]);
  const [saveError, setSaveError] = useState(null);

  // Populate the individual/address dropdowns for whichever firms are already assigned.
  useEffect(() => {
    const firmIds = Array.from(
      new Set(
        Object.values(LOAN_DETAIL_ROLE_KEYS)
          .map((key) => draft[key]?.firmId)
          .filter(Boolean)
      )
    );
    if (firmIds.length === 0) return;
    Promise.all(firmIds.map((id) => api.getFirm(id).catch(() => null))).then(
      (firms) => {
        const map = {};
        firmIds.forEach((id, index) => {
          if (firms[index]) map[id] = firms[index];
        });
        setFirmDetailsById((prev) => ({ ...prev, ...map }));
      }
    );
    // Intentionally run once on open — firm details are (re)fetched as roles change via handleFirmChange.
  }, []);

  const updateRole = (roleKey, patch) =>
    setDraft((prev) => ({
      ...prev,
      [roleKey]: { ...prev[roleKey], ...patch },
    }));

  const handleFirmChange = async (roleKey, firmSummary) => {
    if (!firmSummary) {
      updateRole(roleKey, { firmId: null, contactId: null, addressId: null });
      return;
    }
    let firm = firmDetailsById[firmSummary.id];
    if (!firm) {
      firm = await api.getFirm(firmSummary.id);
      setFirmDetailsById((prev) => ({ ...prev, [firm.id]: firm }));
    }
    // Default to the firm's main contact and preferred address, resetting when the firm changes (§4b/DF174-175).
    updateRole(roleKey, {
      firmId: firm.id,
      contactId: defaultContactId(firm),
      addressId: defaultAddressId(firm),
    });
  };

  const handleSave = async () => {
    const { valid, missing: problems } = validateLoanDetailsDraft(draft);
    if (!valid) {
      setMissing(problems.map((label) => `${label} is required`));
      return;
    }
    setMissing([]);
    setSaveError(null);
    setSaving(true);
    try {
      const payload = { loanId, quotes: draft.quotes };
      Object.values(LOAN_DETAIL_ROLE_KEYS).forEach((key) => {
        payload[key] = draft[key];
      });
      const saved = await api.saveLoanProfessionalDetails(loanId, payload);
      onSaved(normaliseDetails(loanId, saved));
    } catch (err) {
      setSaveError(err);
      setMissing(
        err.fieldErrors?.length
          ? err.fieldErrors.map((fe) => `${fe.field}: ${fe.message}`)
          : [err.message || "Save failed"]
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <A_SimpleDialog
      open
      onClose={onClose}
      title="Edit Professional Details"
      maxWidth="xl"
      fullWidth
      dialogContent={
        <div className={classes.dialogPaper}>
          <div className={classes.dialogSubtitle}>Loan {loanId}</div>

          {missing.length > 0 && (
            <div className={classes.validationSummary}>
              <InfoIcon fontSize="inherit" /> Please complete the following:{" "}
              {missing.join("; ")}.
            </div>
          )}

          <div className={classes.grid3}>
            {BANK_ROLE_KEYS.map((roleKey) => (
              <RoleEditFields
                key={roleKey}
                roleKey={roleKey}
                label={LOAN_DETAIL_ROLE_LABELS[roleKey]}
                required={LOAN_DETAIL_REQUIRED_ROLES.includes(roleKey)}
                loanId={loanId}
                assignment={draft[roleKey]}
                firm={
                  draft[roleKey].firmId
                    ? firmDetailsById[draft[roleKey].firmId]
                    : null
                }
                onFirmChange={(firm) => handleFirmChange(roleKey, firm)}
                onContactChange={(contactId) =>
                  updateRole(roleKey, { contactId })
                }
                onAddressChange={(addressId) =>
                  updateRole(roleKey, { addressId })
                }
                onDateChange={(dateKey, iso) =>
                  updateRole(roleKey, {
                    dates: { ...draft[roleKey].dates, [dateKey]: iso },
                  })
                }
              />
            ))}
          </div>

          <div className={classes.sectionHeading}>
            Broker, borrower&rsquo;s solicitor, loan administration &amp;
            receiver{" "}
            <span
              style={{
                textTransform: "none",
                letterSpacing: 0,
                fontWeight: 400,
              }}
            >
              · picked from the central Professional Contacts list; chosen
              contact&rsquo;s number &amp; email shown
            </span>
          </div>
          <div className={classes.grid3}>
            {REPOSITORY_ROLE_KEYS.map((roleKey) => (
              <RoleEditFields
                key={roleKey}
                roleKey={roleKey}
                label={LOAN_DETAIL_ROLE_LABELS[roleKey]}
                required={LOAN_DETAIL_REQUIRED_ROLES.includes(roleKey)}
                loanId={loanId}
                assignment={draft[roleKey]}
                firm={
                  draft[roleKey].firmId
                    ? firmDetailsById[draft[roleKey].firmId]
                    : null
                }
                onFirmChange={(firm) => handleFirmChange(roleKey, firm)}
                onContactChange={(contactId) =>
                  updateRole(roleKey, { contactId })
                }
                onAddressChange={(addressId) =>
                  updateRole(roleKey, { addressId })
                }
                onDateChange={() => {}}
              />
            ))}
          </div>

          <div className={classes.sectionHeading}>
            Third party quotes{" "}
            <span
              style={{
                textTransform: "none",
                letterSpacing: 0,
                fontWeight: 400,
              }}
            >
              · pick a firm from the panel and enter the £ value (up to 3 per
              type)
            </span>
          </div>
          <QuotesEditor
            loanId={loanId}
            quotes={draft.quotes}
            onChange={(quotes) => setDraft((prev) => ({ ...prev, quotes }))}
          />

          <div className={classes.hint} style={{ marginTop: 8 }}>
             <InfoIcon fontSize="inherit" /> Only active firms (not &lsquo;no further instructions&rsquo;) appear, via the
            integration with Professional Contacts.
          </div>
          {saveError && !missing.length && (
            <div
              className={classes.validationSummary}
              style={{ marginTop: 10 }}
            >
              {saveError.message ||
                "Something went wrong saving. Please try again."}
            </div>
          )}
        </div>
      }
      dialogActions={
        <>
          <A_Button label="Cancel" onClick={onClose} disabled={saving} />
          <A_SecuredAction permission={pageSecurityActions.UPDATE}>
            <A_Button
              label="Save Professional Details"
              premiumVariant="primary"
              onClick={handleSave}
              disabled={saving}
            />
          </A_SecuredAction>
        </>
      }
    />
  );
};

EditProfessionalDetailsDialog.propTypes = {
  loanId: PropTypes.string.isRequired,
  initialDetails: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default EditProfessionalDetailsDialog;
